import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import { useSnackbar } from 'notistack';
import { STORAGE_KEY_ENUM } from 'enums';
import apiService from 'library/api-service';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { renderDataGridLocaleText } from 'utils';
import {
  selectEnvironmentTypes,
  selectEnvironmentTypesIsEmpty,
  selectEnvironmentTypesLoading,
} from 'store/features/environment-types/selector';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Grid2 as Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import environmentTypesActions from 'store/features/environment-types/actions';
import { CreateEnvironmentTypeDialog, UpdateEnvironmentTypeDialog } from './components';

const EnvironmentTypes = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectEnvironmentTypesLoading);
  const permissionCodes = useAppSelector(selectPermissionCodes);
  const environmentTypes = useAppSelector(selectEnvironmentTypes);
  const isEmptyEnvironmentTypes = useAppSelector(selectEnvironmentTypesIsEmpty);

  // useState hooks
  const [openDeleteEnvironmentType, setOpenDeleteEnvironmentType] = useState(false);
  const [openCreateEnvironmentType, setOpenCreateEnvironmentType] = useState(false);
  const [openUpdateEnvironmentType, setOpenUpdateEnvironmentType] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'environmentTypes.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const refresh = async () => {
    dispatch(environmentTypesActions.getEnvironmentTypes());
  };

  // sync helper functions
  const handleCloseDeleteEnvironmentType = () => setOpenDeleteEnvironmentType(false);
  const handleClickOpenDeleteEnvironmentType = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenDeleteEnvironmentType(true);
  };

  const handleCloseCreateEnvironmentType = () => setOpenCreateEnvironmentType(false);
  const handleClickOpenCreateEnvironmentType = () => setOpenCreateEnvironmentType(true);

  const handleCloseUpdateEnvironmentType = () => setOpenUpdateEnvironmentType(false);
  const handleClickOpenUpdateEnvironmentType = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenUpdateEnvironmentType(true);
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('list-environment-type')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    if (!environmentTypes.length && !isEmptyEnvironmentTypes) {
      dispatch(environmentTypesActions.getEnvironmentTypes());
    }
  }, [environmentTypes, isEmptyEnvironmentTypes, dispatch]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['add-environment-type']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenCreateEnvironmentType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              color="success"
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage id="environmentTypes.createEnvironmentType" defaultMessage="Sunucu Tipi Oluştur" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-environment-type']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenUpdateEnvironmentType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<EditIcon />}
              color="secondary">
              <FormattedMessage id="environmentTypes.updateEnvironmentType" defaultMessage="Sunucu Tipi Düzenle" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-environment-type']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenDeleteEnvironmentType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error">
              <FormattedMessage id="environmentTypes.deleteEnvironmentType" defaultMessage="Sunucu Tipi Sil" />
            </Button>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateEnvironmentTypeDialog
        open={openCreateEnvironmentType}
        handleClose={handleCloseCreateEnvironmentType}
        refresh={refresh}
      />
      <UpdateEnvironmentTypeDialog
        open={openUpdateEnvironmentType}
        environmentTypeId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateEnvironmentType}
        refresh={refresh}
      />
      <DeleteDialog
        open={openDeleteEnvironmentType}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteEnvironmentType}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="environmentTypes.deleteEnvironmentType" defaultMessage="Sunucu Tipi Sil" />}
        subTitle={
          <FormattedMessage
            id="environmentTypes.deleteEnvironmentTypeRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra sunucu tipi veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteEnvironmentType}
        successMessage={
          <FormattedMessage
            id="environmentTypes.deleteEnvironmentTypeCompleted"
            defaultMessage="Seçili sunucu tipini silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={environmentTypes}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default EnvironmentTypes;
